<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 class="mb-1">Голосования (Опросы)</h5>
          <p class="text-muted mb-0">Интерактивные опросы зрителей во время трансляции.</p>
        </div>
        <b-button variant="primary" @click="showCreateModal">
          <i class="fa fa-plus mr-1"></i> Создать голосование
        </b-button>
      </div>

      <div v-if="isLoading" class="loading-wrapper">
        <b-spinner class="spinner"></b-spinner>
      </div>
      <b-table
        v-else
        responsive
        :items="votes"
        :fields="fields"
        show-empty
        empty-text="Нет голосований"
      >
        <template #cell(title)="row">
          {{ row.item.title && row.item.title.ru || '—' }}
        </template>
        <template #cell(all_votes_count)="row">
          {{ row.item.all_votes_count || 0 }}
        </template>
        <template #cell(answers_count)="row">
          {{ row.item.vote_answers ? row.item.vote_answers.length : 0 }}
        </template>
        <template #cell(status)="row">
          <b-badge :variant="isVoteActive(row.item) ? 'success' : 'secondary'">
            {{ isVoteActive(row.item) ? 'Активно' : 'Завершено' }}
          </b-badge>
        </template>
        <template #cell(end_at)="row">
          {{ row.item.end_at || '—' }}
        </template>
        <template #cell(actions)="row">
          <div class="d-flex justify-content-end align-items-center">
            <b-button
              v-if="isVoteActive(row.item)"
              size="sm"
              variant="warning"
              @click="finishVote(row.item.id)"
              class="mr-2"
              title="Завершить"
            >
              <i class="fa fa-stop"></i>
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              @click="editVote(row.item)"
              class="mr-2"
              title="Редактировать"
            >
              <i class="fa fa-edit"></i>
            </b-button>
            <b-button
              size="sm"
              variant="danger"
              @click="deleteVote(row.item.id)"
              title="Удалить"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Создание / Редактирование -->
    <b-modal v-model="showForm" :title="formMode === 'create' ? 'Новое голосование' : 'Редактировать голосование'" size="lg">
      <b-form @submit.prevent="saveVote">
        <b-tabs content-class="mt-3" small>
          <b-tab v-for="lang in langs" :key="lang" :title="lang.toUpperCase()">
            <div class="form-group">
              <label class="font-weight-bold">Заголовок ({{ lang.toUpperCase() }})</label>
              <b-form-input v-model="form.title[lang]" :placeholder="`Заголовок ${lang.toUpperCase()}`"></b-form-input>
            </div>
            <div class="form-group">
              <label class="font-weight-bold">Описание ({{ lang.toUpperCase() }})</label>
              <b-form-textarea v-model="form.description[lang]" :placeholder="`Описание ${lang.toUpperCase()}`" rows="2"></b-form-textarea>
            </div>
          </b-tab>
        </b-tabs>

        <div class="row mt-3">
          <div class="col-md-4">
            <div class="form-group">
              <label class="font-weight-bold">Начало</label>
              <b-form-input v-model="form.start_at" type="datetime-local"></b-form-input>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="font-weight-bold">Окончание</label>
              <b-form-input v-model="form.end_at" type="datetime-local"></b-form-input>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="font-weight-bold">Часовой пояс</label>
              <b-form-select v-model="form.timezone" :options="timezoneOptions"></b-form-select>
            </div>
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="font-weight-bold">Варианты ответов</label>
          <div v-for="(answer, index) in form.vote_answers" :key="index" class="d-flex align-items-center mb-2">
            <span class="mr-2 text-muted font-weight-bold">{{ index + 1 }}.</span>
            <b-form-input v-model="answer.value.text" :placeholder="`Вариант ${index + 1}`" class="mr-2"></b-form-input>
            <b-button
              v-if="form.vote_answers.length > 2"
              size="sm"
              variant="outline-danger"
              @click="removeAnswer(index)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
          <b-button variant="outline-secondary" size="sm" @click="addAnswer" class="mt-2">
            <i class="fa fa-plus mr-1"></i> Добавить вариант
          </b-button>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showForm = false">Отмена</b-button>
        <b-button variant="primary" @click="saveVote" :disabled="!isFormValid">Сохранить</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  name: "WatchPolls",
  data() {
    return {
      votes: [],
      isLoading: false,
      showForm: false,
      formMode: 'create',
      langs: ['ru', 'en', 'kk', 'uz'],
      form: this.getEmptyForm(),
      fields: [
        {key: 'id', label: 'ID'},
        {key: 'title', label: 'Вопрос'},
        {key: 'all_votes_count', label: 'Голосов'},
        {key: 'answers_count', label: 'Вариантов'},
        {key: 'end_at', label: 'Окончание'},
        {key: 'status', label: 'Статус'},
        {key: 'actions', label: 'Действия', class: 'text-right'}
      ],
      timezoneOptions: [
        {value: 'Europe/Moscow', text: 'Europe/Moscow (MSK)'},
        {value: 'Asia/Almaty', text: 'Asia/Almaty (+6)'},
        {value: 'Asia/Tashkent', text: 'Asia/Tashkent (+5)'},
      ]
    }
  },
  computed: {
    isFormValid() {
      const hasTitle = this.form.title.ru;
      const hasAnswers = this.form.vote_answers.filter(a => a.value && a.value.text).length >= 2;
      const hasDates = this.form.start_at && this.form.end_at;
      return hasTitle && hasAnswers && hasDates;
    }
  },
  methods: {
    getEmptyForm() {
      return {
        title: {ru: '', en: '', kk: '', uz: ''},
        description: {ru: '', en: '', kk: '', uz: ''},
        start_at: '',
        end_at: '',
        timezone: 'Europe/Moscow',
        vote_answers: [
          {value: {text: ''}},
          {value: {text: ''}},
        ]
      };
    },
    formatDateForInput(dateStr) {
      if (!dateStr) return '';
      return dateStr.replace(' ', 'T').slice(0, 16);
    },
    formatDateForApi(dateStr) {
      if (!dateStr) return '';
      // datetime-local дает "2025-09-29T14:39" — нужно "2025-09-29 14:39:00"
      const clean = dateStr.replace('T', ' ');
      return clean.length === 16 ? clean + ':00' : clean.slice(0, 19);
    },
    isVoteActive(vote) {
      if (!vote.end_at) return false;
      return new Date(vote.end_at) > new Date();
    },
    showCreateModal() {
      this.formMode = 'create';
      this.form = this.getEmptyForm();
      this.showForm = true;
    },
    editVote(vote) {
      this.formMode = 'edit';
      this.form = {
        id: vote.id,
        title: {...(vote.title || {ru: '', en: '', kk: '', uz: ''})},
        description: {...(vote.description || {ru: '', en: '', kk: '', uz: ''})},
        start_at: this.formatDateForInput(vote.start_at),
        end_at: this.formatDateForInput(vote.end_at),
        timezone: vote.timezone || 'Europe/Moscow',
        vote_answers: (vote.vote_answers || []).map(a => ({
          ...a,
          value: {...(a.value || {text: ''})}
        }))
      };
      if (this.form.vote_answers.length < 2) {
        while (this.form.vote_answers.length < 2) {
          this.form.vote_answers.push({value: {text: ''}});
        }
      }
      this.showForm = true;
    },
    addAnswer() {
      this.form.vote_answers.push({value: {text: ''}});
    },
    removeAnswer(index) {
      this.form.vote_answers.splice(index, 1);
    },
    buildPayload() {
      const base = {
        title: this.form.title,
        description: this.form.description,
        start_at: this.formatDateForApi(this.form.start_at),
        end_at: this.formatDateForApi(this.form.end_at),
        timezone: this.form.timezone,
      };

      if (this.formMode === 'create') {
        base.answers = this.form.vote_answers
          .map(a => a.value.text)
          .filter(Boolean);
      } else {
        base.answers = this.form.vote_answers.filter(a => a.value && a.value.text);
        base.vote_answers = this.form.vote_answers.filter(a => a.value && a.value.text);
      }

      return base;
    },
    async fetchVotes() {
      try {
        const response = await api.votes.index();
        this.votes = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error fetching votes:', error);
      }
    },
    async saveVote() {
      if (!this.isFormValid) return;

      try {
        const payload = this.buildPayload();
        if (this.formMode === 'create') {
          await api.votes.store(payload);
          this.$toast.success('Голосование создано', {position: 'top-right', timeout: 2000});
        } else {
          await api.votes.update(this.form.id, payload);
          this.$toast.success('Голосование обновлено', {position: 'top-right', timeout: 2000});
        }
        this.showForm = false;
        await this.fetchVotes();
      } catch (error) {
        console.error('Error saving vote:', error);
        this.$toast.error('Ошибка при сохранении', {position: 'top-right'});
      }
    },
    async finishVote(id) {
      try {
        await api.votes.finish(id);
        this.$toast.success('Голосование завершено', {position: 'top-right', timeout: 2000});
        await this.fetchVotes();
      } catch (error) {
        console.error('Error finishing vote:', error);
      }
    },
    async deleteVote(id) {
      if (!confirm('Удалить голосование?')) return;
      try {
        await api.votes.delete(id);
        this.$toast.success('Голосование удалено', {position: 'top-right', timeout: 2000});
        await this.fetchVotes();
      } catch (error) {
        console.error('Error deleting vote:', error);
      }
    }
  },
  created() {
    this.isLoading = true;
    this.fetchVotes().finally(() => {
      this.isLoading = false;
    });
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}
</style>
